import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import type { ProviderDisclaimerModel, ProviderDisclaimerRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['providerDisclaimers']

type ProviderDisclaimersFilters = {
  provider_id?: number,
}

type UseProviderDisclaimersOptions = {
  entityKey?: string,
  filters?: ProviderDisclaimersFilters,
  performHttpRequests?: boolean,
  requestOptions?: ProviderDisclaimerRequestOptions,
}

function useProviderDisclaimers(options: UseProviderDisclaimersOptions = {}) {
  const { entityKey, filters, performHttpRequests } = options

  const {
    provider_id: filterProviderId,
  } = filters || {}

  const {
    updatedEntities: {
      providerDisclaimers: providerDisclaimersUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { loading: loadingProviderDisclaimers } = useReduxAction(
    'providerDisclaimers',
    'loadProviderDisclaimers',
    {
      entityKey,
      ...defaultRequestOptions.providerDisclaimer,
      ...filters,
    },
    [performHttpRequests, filters],
    {
      // @ts-expect-error TS2322
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  const { providerDisclaimers } = useSelector(reduxState => reduxState.entities)

  const filteredProviderDisclaimers = useMemo(() => {
    const filtered = Object.values(providerDisclaimers).filter((providerDisclaimer: ProviderDisclaimerModel) => {
      const { provider_id } = providerDisclaimer

      const matchProviderId = matchFilterNumber(provider_id, filterProviderId)

      return matchProviderId
    })

    return sortArrayBy(filtered, 'asc')
  }, [providerDisclaimersUpdatedAt, JSON.stringify(filters)])

  const hasFilteredProviderDisclaimers = !!filteredProviderDisclaimers.length

  return {
    filteredProviderDisclaimers,
    hasFilteredProviderDisclaimers,
    loading: loadingProviderDisclaimers,
  }
}

export default useProviderDisclaimers
